import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { ArrowDown } from 'lucide-react';
import DotField from '../UI/DotField';

gsap.registerPlugin(ScrollTrigger, useGSAP);

// Things from the resume, phrased as asks
const ASKS = [
  'build a rejection dashboard',
  'automate the MIS report',
  'model the loan portfolio',
  'map every branch on one screen',
  'price a gold loan in seconds',
  'run UAT before go-live',
  'prototype it with GenAI',
  'make the data tell a story',
  'simplify the whole process',
];

/**
 * Pinned scroll section: the list of asks scrolls through a fixed
 * "akash, can you" label — the centered line is bright, the rest fade
 * with distance. Backed by a cursor-reactive dot field.
 */
const AskMe: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useGSAP(
    () => {
      const items = gsap.utils.toArray<HTMLElement>('.ask-item');
      if (!items.length) return;

      const setStates = (progress: number) => {
        const pos = progress * (items.length - 1);
        items.forEach((el, i) => {
          const dist = Math.abs(i - pos);
          gsap.set(el, {
            opacity: Math.max(0.07, 1 - dist * 0.42),
            filter: `blur(${Math.min(dist * 1.1, 3.5)}px)`,
          });
        });
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setStates(0);
        return;
      }

      let itemH = items[0].offsetHeight;

      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top top',
        end: () => `+=${items.length * window.innerHeight * 0.45}`,
        pin: true,
        scrub: true,
        invalidateOnRefresh: true,
        onRefresh: () => {
          itemH = items[0].offsetHeight;
        },
        onUpdate: self => {
          const pos = self.progress * (items.length - 1);
          gsap.set(listRef.current, { y: -pos * itemH });
          setStates(self.progress);
        },
      });
      setStates(0);

      // Pinned distances depend on the webfont's line height
      document.fonts?.ready.then(() => ScrollTrigger.refresh());
    },
    { scope: sectionRef }
  );

  return (
    <section ref={sectionRef} id='ask' className='relative bg-carbon text-chalk overflow-hidden'>
      <div className='relative h-screen'>
        {/* Cursor-reactive dot field */}
        <DotField
          className='absolute inset-0'
          baseColor='112,107,99'
          glowColor='255,45,0'
          gap={26}
          influence={160}
        />
        {/* Soft vignette so the centered line stays readable */}
        <div
          className='absolute inset-0 pointer-events-none'
          style={{
            background:
              'radial-gradient(ellipse 90% 55% at 50% 50%, rgba(15,14,12,0.82) 0%, rgba(15,14,12,0.35) 55%, rgba(15,14,12,0) 100%)',
          }}
        />

        <div className='relative z-10 h-full flex flex-col'>
          <div className='label-mono text-chalk/40 pt-24 sm:pt-28 flex items-center justify-center gap-2'>
            <span className='text-accent'>[</span> Go ahead, ask me{' '}
            <span className='text-accent'>]</span>
          </div>

          <div className='flex-1 flex flex-col md:flex-row items-center justify-center gap-2 md:gap-5 px-5'>
            <div className='font-serif italic text-chalk/90 whitespace-nowrap text-[clamp(0.95rem,4.7vw,3.6rem)] md:text-right'>
              akash, can you
            </div>
            <div className='relative h-[1.55em] overflow-hidden md:overflow-visible text-[clamp(0.9rem,4.5vw,3.4rem)]'>
              <ul ref={listRef} className='will-change-transform'>
                {ASKS.map(ask => (
                  <li
                    key={ask}
                    className='ask-item whitespace-nowrap font-medium tracking-tight leading-[1.55]'
                  >
                    {ask}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className='label-mono text-chalk/40 pb-10 flex items-center justify-center gap-2'>
            Keep scrolling
            <ArrowDown size={12} className='text-accent animate-bounce' />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AskMe;
